import React from 'react';
import { Check, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type PlanHighlight =
  | string
  | { label: string; accent?: 'emerald' | 'sky'; tooltip?: React.ReactNode };

interface PlanCardProps {
  name: string;
  price: string;
  billingPeriod: string;
  perMonthPrice?: React.ReactNode;
  description: React.ReactNode;
  isPopular?: boolean;
  isPro?: boolean;
  highlights: PlanHighlight[];
  buttonText: string;
  buttonDisabled: boolean;
  onUpgrade: () => void;
  trialElement?: React.ReactNode;
  isElite?: boolean;
  /** Gemini allowance line rendered on the card face in Gemini brand gradient. */
  geminiLine?: string;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  name,
  price,
  billingPeriod,
  perMonthPrice,
  description,
  isPopular,
  isPro,
  highlights,
  buttonText,
  buttonDisabled,
  onUpgrade,
  trialElement,
  isElite = false,
  geminiLine,
}) => {
  return (
    <div className={`relative flex flex-col bg-zinc-900/60 backdrop-blur-sm border rounded-xl p-6 sm:p-8 transition-all duration-300 ${
      isElite
        ? 'border-amber-500/40 shadow-lg shadow-amber-500/10'
        : isPro
        ? 'border-cyan-500/40 shadow-lg shadow-cyan-500/10'
        : 'border-emerald-500/25'
    }`}>
      {/* Tier hairline glow along the top edge */}
      <div
        className={`pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent ${
          isElite ? 'via-amber-400/70' : isPro ? 'via-cyan-400/70' : 'via-emerald-400/50'
        }`}
      />
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-zinc-950 text-xs font-bold px-4 py-1 rounded-md shadow-lg shadow-black/40 whitespace-nowrap">
          MOST POPULAR
        </div>
      )}

      {/* Plan name + description */}
      <h3 className={`text-xl font-bold mb-1 ${isElite ? 'text-amber-200' : isPro ? 'text-cyan-200' : 'text-emerald-200'}`}>{name}</h3>
      <p className="text-sm text-zinc-100 mb-5 min-h-[40px]">{description}</p>

      {/* Price */}
      <div className="flex items-baseline mb-1">
        <span className="text-lg font-medium text-zinc-100">$</span>
        <span className="text-4xl sm:text-5xl font-bold text-white mx-0.5">{price}</span>
        <span className="text-sm text-zinc-200 ml-1">{billingPeriod}</span>
      </div>
      {perMonthPrice && (
        <p className="text-xs text-zinc-100 mb-3">{perMonthPrice}</p>
      )}
      {!perMonthPrice && <div className="mb-3" />}

      {/* Gemini allowance, in Gemini brand gradient (blue -> purple -> pink) */}
      {geminiLine ? (
        <p className="mb-4 text-[13px] font-semibold bg-gradient-to-r from-sky-400 via-violet-400 to-pink-400 bg-clip-text text-transparent">
          {geminiLine}
        </p>
      ) : (
        <div className="mb-4" />
      )}

      {/* CTA button */}
      <button
        onClick={onUpgrade}
        disabled={buttonDisabled}
        className={`w-full py-3 px-4 rounded-md text-sm font-semibold transition-all duration-200 ${
          buttonDisabled
            ? 'bg-zinc-800/60 border border-zinc-700 text-zinc-200 cursor-default'
            : isElite
            ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 hover:from-amber-300 hover:to-amber-400 hover:shadow-lg hover:shadow-amber-500/25'
            : isPro
            ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-zinc-950 hover:from-cyan-300 hover:to-cyan-400 hover:shadow-lg hover:shadow-cyan-500/25'
            : 'bg-zinc-800 border border-emerald-500/40 text-emerald-200 hover:bg-zinc-700 hover:border-emerald-400/60'
        }`}
      >
        {buttonText}
      </button>

      {/* Trial button (directly under CTA) */}
      {trialElement && (
        <div className="mt-2 min-h-[32px] flex items-center justify-center">
          {trialElement}
        </div>
      )}
      {!trialElement && <div className="mt-2 min-h-[32px]" />}

      {/* Divider */}
      <div className="border-t border-zinc-800 my-5" />

      {/* Key highlights */}
      <TooltipProvider delayDuration={150}>
        <ul className="space-y-3 flex-1">
          {highlights.map((item, idx) => {
            const isObj = typeof item !== 'string';
            const label = isObj ? item.label : item;
            const accent = isObj && item.accent === 'sky' ? 'sky' : 'emerald';
            const tooltip = isObj ? item.tooltip : undefined;
            const checkColor =
              accent === 'sky' ? 'text-cyan-400' : 'text-emerald-400';
            return (
              <li key={idx} className="flex items-start text-sm text-white">
                <Check
                  size={16}
                  className={`${checkColor} mr-2.5 mt-0.5 shrink-0`}
                />
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span>{label}</span>
                  {tooltip && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="More info"
                          className="inline-flex items-center justify-center text-cyan-500/60 hover:text-cyan-300 transition-colors"
                          onClick={(e) => e.preventDefault()}
                        >
                          <HelpCircle size={13} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        className="z-[100] max-w-[280px] text-xs leading-relaxed"
                      >
                        {tooltip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </TooltipProvider>
    </div>
  );
};
